import { useEffect, useState } from 'react';

import { Box, Divider, Heading } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import Head from 'next/head';
import { useRouter } from 'next/router';

import {
  ILoginFormSubmitProps,
  LoginForm,
} from '@r-client/admin/feature/login';
import {
  E_SIGN_IN_ERROR_CODES,
  ISignInResult,
  useAuth,
} from '@r-client/shared/feature/auth';
import { Spinner } from '@r-client/shared/ui/spinner';

function getErrorMessage(result: ISignInResult): string {
  switch (result?.error?.code) {
    case E_SIGN_IN_ERROR_CODES.BAD_USER_INPUT:
      return 'Invalid email or password';
    case E_SIGN_IN_ERROR_CODES.DOWNSTREAM_SERVICE_ERROR:
      return 'Please check that the email is valid and the password meets the requirements';
    default:
      return 'Something went wrong; please try again later';
  }
}

export const Login = observer(function Login() {
  const { signIn, viewer, isLoading } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  useEffect(() => {
    // Update `viewer.info.isAdmin` once finalized
    // If user session exist with authorized then pass him to dashboard
    if (!isLoading && viewer?.isAdmin) {
      router.push('/dashboard');
    }
  }, [isLoading, router, viewer]);
  const handleFormSubmit = async ({
    email,
    password,
  }: ILoginFormSubmitProps) => {
    setLoading(true);
    const result = await signIn({
      email,
      password,
      redirectTo: '/dashboard',
    });

    if (!result.success) {
      setLoading(false);
      setError(getErrorMessage(result));
    }
  };
  return isLoading || viewer?.isAuthenticated ? (
    <Spinner fullscreen animatedLogo="line" />
  ) : (
    <Box
      p="16px"
      display="flex"
      justifyContent="center"
      alignItems="center"
      h={{ base: '100vh', md: '100%' }}
    >
      <Head>
        <title>Log in — Republic Admin</title>
      </Head>
      {loading && <Spinner fullscreen animatedLogo="line" />}
      <Box
        w={{ base: 'full', md: 'sm' }}
        py="8"
        px="4"
        backgroundColor="white"
        boxShadow="lg"
      >
        <Heading textAlign="center" size="xl">
          Log in
        </Heading>
        <LoginForm onSubmit={handleFormSubmit} errorMessage={error} />
        <Divider my="3" />
      </Box>
    </Box>
  );
});

export default Login;
